import { useMemo, useRef, useState } from 'react';
import { useAdminTenants } from '../../contexts/AdminEventsProvider';
import { tenantsService, type TenantView } from '../../services/tenants/tenants.service';

type PendingModeChange = {
  tenants: TenantView[];
  maintenanceMode: boolean;
};

export function useMaintenancePageController() {
  const { tenants, isLoading, error } = useAdminTenants();
  const [pendingModeChange, setPendingModeChange] = useState<PendingModeChange | null>(null);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedTenantIds, setSelectedTenantIds] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const mutationLockRef = useRef(false);
  const selectableTenants = useMemo(
    () => tenants.filter((tenant) => !tenant.maintenanceMode),
    [tenants],
  );
  const allTenantsInMaintenance = tenants.length > 0 && selectableTenants.length === 0;

  const requestModeChange = (tenant: TenantView) => {
    setSubmitError(null);
    setPendingModeChange({ tenants: [tenant], maintenanceMode: !tenant.maintenanceMode });
  };

  const closeConfirmation = () => {
    if (!isSubmitting) {
      setPendingModeChange(null);
    }
  };

  const toggleSelectMode = () => {
    setIsSelectMode((current) => !current);
    setSelectedTenantIds([]);
    setSubmitError(null);
  };

  const toggleTenantSelection = (tenantId: number) => {
    setSelectedTenantIds((current) => (
      current.includes(tenantId)
        ? current.filter((id) => id !== tenantId)
        : [...current, tenantId]
    ));
  };

  const selectAllTenants = () => {
    setSelectedTenantIds(selectableTenants.map((tenant) => tenant.id));
  };

  const requestSelectedMaintenance = () => {
    const selected = selectableTenants.filter((tenant) => selectedTenantIds.includes(tenant.id));
    if (selected.length > 0) {
      setSubmitError(null);
      setPendingModeChange({ tenants: selected, maintenanceMode: true });
    }
  };

  const requestCancelAllMaintenance = () => {
    if (allTenantsInMaintenance) {
      setSubmitError(null);
      setPendingModeChange({ tenants, maintenanceMode: false });
    }
  };

  const confirmModeChange = async () => {
    if (!pendingModeChange || mutationLockRef.current) return;

    mutationLockRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      for (const tenant of pendingModeChange.tenants) {
        await tenantsService.updateMaintenanceMode(tenant.id, {
          maintenanceMode: pendingModeChange.maintenanceMode,
        });
      }
      setPendingModeChange(null);
      setSelectedTenantIds([]);
      setIsSelectMode(false);
    } catch (requestError: unknown) {
      setSubmitError(requestError instanceof Error ? requestError.message : 'עדכון מצב התחזוקה נכשל');
    } finally {
      mutationLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  return {
    tenants,
    isLoading,
    error: error ?? submitError,
    pendingModeChange,
    isSubmitting,
    isSelectMode,
    selectedTenantIds,
    selectableTenants,
    allTenantsInMaintenance,
    requestModeChange,
    closeConfirmation,
    toggleSelectMode,
    toggleTenantSelection,
    selectAllTenants,
    requestSelectedMaintenance,
    requestCancelAllMaintenance,
    confirmModeChange,
  };
}
